import { Injectable } from '@angular/core';
import {
	collection,
	doc,
	Firestore,
	getDocs,
	limit,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
} from '@angular/fire/firestore';
import { DateTimeUtils, parseMasLocalDateTime } from '../utilities';

/** Default cap for account history reads (`mas-accounts-history`). */
export const MAS_ACCOUNT_HISTORY_READ_LIMIT = 50;

export type MasAccountHistoryWrite = {
	changedBy: string;
	caller: string;
	changes: Record<string, unknown>;
	autofix?: Record<string, unknown>;
};

/**
 * Shared audit layer for `mas-accounts/{accountId}/mas-accounts-history`.
 * All client history writes and reads should go through this service.
 */
@Injectable({
	providedIn: 'root',
})
export class MasAccountAuditService {
	async append(firestore: Firestore, accountId: string, entry: MasAccountHistoryWrite): Promise<void> {
		if (!accountId || !entry.changedBy || Object.keys(entry.changes).length === 0) {
			return;
		}

		const historyRef = doc(collection(firestore, `mas-accounts/${accountId}/mas-accounts-history`));
		const payload: Record<string, unknown> = {
			timestamp: serverTimestamp(),
			changedBy: entry.changedBy,
			caller: entry.caller,
			changes: entry.changes,
		};
		if (entry.autofix && Object.keys(entry.autofix).length > 0) {
			payload['autofix'] = entry.autofix;
		}

		await setDoc(historyRef, payload);
	}

	async loadFormatted(
		firestore: Firestore,
		accountId: string,
		readLimit: number = MAS_ACCOUNT_HISTORY_READ_LIMIT,
	): Promise<{ key: string; value: Record<string, unknown> }[]> {
		const historyRef = collection(firestore, `mas-accounts/${accountId}/mas-accounts-history`);
		const q = query(historyRef, orderBy('timestamp', 'desc'), limit(readLimit));
		const snapshot = await getDocs(q);

		return snapshot.docs.map(docSnap => {
			const data = docSnap.data() as Record<string, unknown>;
			const dateForKey = parseMasLocalDateTime(data['timestamp']) ?? new Date();

			return {
				key: `${DateTimeUtils.formatDate(dateForKey, 'date')}${DateTimeUtils.formatDate(dateForKey, 'time24')}`,
				value: data,
			};
		});
	}
}
